import React, {
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";

import { fetchViaPost } from "../helpers/fetchViaPost";
import { LeagueDisplay } from "./LeagueDisplay";
import { LeagueTablePanel } from "./LeagueTablePanel";
import { TeamStatisticsTable } from "./TeamStatisticsTable";


function normalizeExpectedValues(
  values = {}
) {
  return {
    games_played:
      Number(
        values.expected_games_played ||
        0
      ),

    won:
      Number(
        values.expected_won ||
        0
      ),

    draw:
      Number(
        values.expected_draw ||
        0
      ),

    lost:
      Number(
        values.expected_lost ||
        0
      ),

    goals_scored:
      Number(
        values.expected_goals_scored ||
        0
      ),

    goals_against:
      Number(
        values.expected_goals_against ||
        0
      ),

    points:
      Number(
        values.expected_points ||
        0
      ),
  };
}


function normalizeExpectedTable(
  rows = []
) {
  return (
    Array.isArray(rows)
      ? rows
      : []
  ).map(
    (row) => ({
      ...row,

      overall:
        normalizeExpectedValues(
          row?.overall
        ),

      home:
        normalizeExpectedValues(
          row?.home
        ),

      away:
        normalizeExpectedValues(
          row?.away
        ),
    })
  );
}


function ResultRow({
  label,
  value,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          "1fr auto",
        gap: 2,
      }}
    >
      <Typography>
        {label}
      </Typography>

      <Typography
        sx={{
          fontVariantNumeric:
            "tabular-nums",
          fontWeight: 600,
        }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}


export function AdminToolsDialog({
  open,
  onClose,
  onOpenSeasonBaseStats,
  leagues = [],
}) {
  const [
    leagueReferenceInventory,
    setLeagueReferenceInventory,
  ] = useState(null);

  const [
    loadingLeagueReferenceInventory,
    setLoadingLeagueReferenceInventory,
  ] = useState(false);

  const [
    transitionDiagnosis,
    setTransitionDiagnosis,
  ] = useState(null);

  const [
    loadingTransitionDiagnosis,
    setLoadingTransitionDiagnosis,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    userSeasonDiagnosis,
    setUserSeasonDiagnosis,
  ] = useState(null);

  const [
    loadingUserSeasonDiagnosis,
    setLoadingUserSeasonDiagnosis,
  ] = useState(false);

  const [
    transitionLeagueId,
    setTransitionLeagueId,
  ] = useState("");

  const [
    recalculatingTransition,
    setRecalculatingTransition,
  ] = useState(false);

  const [
    transitionRecalculateResult,
    setTransitionRecalculateResult,
  ] = useState(null);

  const [
    expectedLeagueId,
    setExpectedLeagueId,
  ] = useState("");

  const [
    expectedSeasonId,
    setExpectedSeasonId,
  ] = useState("");

  const [
    expectedSeasons,
    setExpectedSeasons,
  ] = useState([]);

  const [
    loadingExpectedSeasons,
    setLoadingExpectedSeasons,
  ] = useState(false);

  const [
    expectedDiagnosis,
    setExpectedDiagnosis,
  ] = useState(null);

  const [
    expectedSeasonState,
    setExpectedSeasonState,
  ] = useState(null);

  const [
    expectedSeasonInfoOpen,
    setExpectedSeasonInfoOpen,
  ] = useState(false);

  const [
    loadingExpectedDiagnosis,
    setLoadingExpectedDiagnosis,
  ] = useState(false);

  const [
    buildingExpectedTable,
    setBuildingExpectedTable,
  ] = useState(false);

  const [
    expectedTableResult,
    setExpectedTableResult,
  ] = useState(null);

  const [
    compilingMissingExpected,
    setCompilingMissingExpected,
  ] = useState(false);

  const [
    expectedCompileProgress,
    setExpectedCompileProgress,
  ] = useState({
    current: 0,
    total: 0,
  });

  const [
    expectedCompileResult,
    setExpectedCompileResult,
  ] = useState(null);


  const [
    expectedDetailedStatsOpen,
    setExpectedDetailedStatsOpen,
  ] = useState(false);

  const [
    calculatingExpectedDetailedStats,
    setCalculatingExpectedDetailedStats,
  ] = useState(false);

  const [
    expectedDetailedStatsResult,
    setExpectedDetailedStatsResult,
  ] = useState(null);


  const [
    expectedSimulationDrawFactor,
    setExpectedSimulationDrawFactor,
  ] = useState("0.92");

  const [
    expectedSimulationRuns,
    setExpectedSimulationRuns,
  ] = useState("1000");

  const [
    teamStatsLeagueId,
    setTeamStatsLeagueId,
  ] = useState("");

  const [
    teamStatsSeasonId,
    setTeamStatsSeasonId,
  ] = useState("");

  const [
    teamStatsSeasons,
    setTeamStatsSeasons,
  ] = useState([]);

  const [
    loadingTeamStatsSeasons,
    setLoadingTeamStatsSeasons,
  ] = useState(false);

  const [
    loadingTeamStats,
    setLoadingTeamStats,
  ] = useState(false);

  const [
    teamStatsResult,
    setTeamStatsResult,
  ] = useState(null);

  const [
    teamStatsMode,
    setTeamStatsMode,
  ] = useState("overall");

  const [
    reloadingMatchStatistics,
    setReloadingMatchStatistics,
  ] = useState(false);

  const [
    matchStatisticsReloadProgress,
    setMatchStatisticsReloadProgress,
  ] = useState({
    current: 0,
    total: 0,
    current_label: "",
    fixtures_expected: 0,
    fixtures_with_statistics: 0,
    fixtures_complete: 0,
    errors: 0,
  });

  const [
    matchStatisticsReloadResult,
    setMatchStatisticsReloadResult,
  ] = useState(null);

  const [actualRepairItems, setActualRepairItems] = useState([]);
  const [actualRepairPhase, setActualRepairPhase] = useState("");
  const [actualRepairProgress, setActualRepairProgress] = useState({ current: 0, total: 0 });


  async function diagnoseUserSeasonStats() {
    setLoadingUserSeasonDiagnosis(true);
    setUserSeasonDiagnosis(null);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "diagnose-user-season-stats",
          JSON.stringify({})
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "USER_SEASON_STATS konnten nicht geprüft werden."
        );

        return;
      }

      setUserSeasonDiagnosis(
        result
      );

    } catch (err) {
      console.error(
        "[Admin Tools] USER_SEASON_STATS diagnosis",
        err
      );

      setError(
        err?.message ||
          "USER_SEASON_STATS konnten nicht geprüft werden."
      );

    } finally {
      setLoadingUserSeasonDiagnosis(false);
    }
  }


  async function loadExpectedSeasons(
    leagueIdValue
  ) {
    const leagueId =
      Number(
        leagueIdValue
      );

    setExpectedSeasonId("");
    setExpectedSeasons([]);
    setExpectedDiagnosis(null);
    setExpectedSeasonState(null);
    setExpectedSeasonInfoOpen(false);
    setExpectedTableResult(null);
    setExpectedCompileResult(null);
    setExpectedCompileProgress({
      current: 0,
      total: 0,
    });

    if (
      !Number.isFinite(
        leagueId
      ) ||
      leagueId <= 0
    ) {
      return;
    }

    setLoadingExpectedSeasons(true);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "available_seasons",
            league_id:
              leagueId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "Verfügbare Saisons konnten nicht geladen werden."
        );
        return;
      }

      const seasons =
        Array.isArray(
          result?.seasons
        )
          ? result.seasons
          : [];

      setExpectedSeasons(
        seasons
      );

      if (
        seasons.length === 1
      ) {
        const seasonId =
          Number(
            seasons[0]
              ?.season_id
          );

        setExpectedSeasonId(
          seasonId
        );

        await loadExpectedSeasonState(
          leagueId,
          seasonId
        );
      }
    } catch (err) {
      console.error(
        "[Admin Tools] Expected seasons",
        err
      );

      setError(
        err?.message ||
          "Verfügbare Saisons konnten nicht geladen werden."
      );
    } finally {
      setLoadingExpectedSeasons(false);
    }
  }


  async function loadExpectedSeasonState(
    leagueIdValue,
    seasonIdValue
  ) {
    const leagueId =
      Number(
        leagueIdValue
      );

    const seasonId =
      Number(
        seasonIdValue
      );

    setExpectedDiagnosis(null);
    setExpectedSeasonState(null);
    setExpectedSeasonInfoOpen(false);
    setExpectedTableResult(null);

    if (
      !Number.isFinite(
        leagueId
      ) ||
      !Number.isFinite(
        seasonId
      )
    ) {
      return;
    }

    setLoadingExpectedDiagnosis(true);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "load",

            league_id:
              leagueId,

            season_id:
              seasonId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
          "Expected Table konnte nicht geladen werden."
        );
        return;
      }

      setExpectedSeasonState(
        result
      );

      if (result?.record) {
        setExpectedTableResult({
          success: true,

          message:
            result?.message ||
            "Gespeicherte Expected Table geladen.",

          record:
            result.record,
        });
      }
    } catch (err) {
      console.error(
        "[Admin Tools] Expected fast load",
        err
      );

      setError(
        err?.message ||
        "Expected Table konnte nicht geladen werden."
      );
    } finally {
      setLoadingExpectedDiagnosis(false);
    }
  }


  async function diagnoseExpectedTable(
    leagueIdValue,
    seasonIdValue
  ) {
    const leagueId =
      Number(
        leagueIdValue
      );

    const seasonId =
      Number(
        seasonIdValue
      );

    setExpectedDiagnosis(null);
    setExpectedTableResult(null);

    if (
      !Number.isFinite(
        leagueId
      ) ||
      !Number.isFinite(
        seasonId
      )
    ) {
      return;
    }

    setLoadingExpectedDiagnosis(true);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "diagnose",
            league_id:
              leagueId,
            season_id:
              seasonId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "Expected-Table-Diagnose fehlgeschlagen."
        );
        return;
      }

      setExpectedDiagnosis(
        result
      );

      setExpectedSeasonInfoOpen(
        true
      );
    } catch (err) {
      console.error(
        "[Admin Tools] Expected diagnosis",
        err
      );

      setError(
        err?.message ||
          "Expected-Table-Diagnose fehlgeschlagen."
      );
    } finally {
      setLoadingExpectedDiagnosis(false);
    }
  }


  async function compileMissingExpectedFixtures() {
    const fixtureIds =
      Array.isArray(
        expectedDiagnosis
          ?.missing_fixture_ids
      )
        ? expectedDiagnosis
            .missing_fixture_ids
            .map(Number)
            .filter(
              Number.isFinite
            )
        : [];

    if (fixtureIds.length === 0) {
      setError(
        "Es gibt keine fehlenden Fixture-Berechnungen zum Nachkompilieren."
      );
      return;
    }

    const leagueId =
      Number(expectedLeagueId);

    const seasonId =
      Number(expectedSeasonId);

    if (
      !Number.isFinite(leagueId) ||
      !Number.isFinite(seasonId)
    ) {
      setError(
        "Liga oder Saison ist ungültig."
      );
      return;
    }

    setCompilingMissingExpected(true);
    setExpectedCompileResult(null);
    setExpectedTableResult(null);
    setError("");

    setExpectedCompileProgress({
      current: 0,
      total: fixtureIds.length,
    });

    try {
      const SLICE_LENGTH = 40;

      let compiledCount = 0;
      const failedFixtureIds = [];

      for (
        let start = 0;
        start < fixtureIds.length;
        start += SLICE_LENGTH
      ) {
        const slice =
          fixtureIds.slice(
            start,
            start + SLICE_LENGTH
          );

        const response =
          await fetchViaPost(
            "compile-fixtures-in-selection",
            JSON.stringify({
              fixture_ids:
                slice,
              skip_missing:
                false,
            })
          );

        const result =
          await response.json();

        if (!result?.success) {
          throw new Error(
            result?.message ||
            "Fehlende Fixtures konnten nicht nachkompiliert werden."
          );
        }

        const compiledResults =
          Array.isArray(
            result?.result
          )
            ? result.result
            : [];

        for (
          const compiled of
          compiledResults
        ) {
          if (
            compiled?.success ===
            false
          ) {
            const fixtureId =
              Number(
                compiled
                  ?.fixture_id
              );

            if (
              Number.isFinite(
                fixtureId
              )
            ) {
              failedFixtureIds.push(
                fixtureId
              );
            }

            continue;
          }

          compiledCount += 1;
        }

        setExpectedCompileProgress({
          current:
            Math.min(
              start +
                slice.length,
              fixtureIds.length
            ),
          total:
            fixtureIds.length,
        });
      }

      setExpectedCompileResult({
        requested:
          fixtureIds.length,

        compiled:
          compiledCount,

        failed_fixture_ids:
          failedFixtureIds,
      });

      await diagnoseExpectedTable(
        leagueId,
        seasonId
      );
    } catch (err) {
      console.error(
        "[Admin Tools] Expected missing compile",
        err
      );

      setError(
        err?.message ||
          "Fehlende Fixtures konnten nicht nachkompiliert werden."
      );
    } finally {
      setCompilingMissingExpected(false);
    }
  }


  async function buildExpectedTable() {
    const leagueId =
      Number(expectedLeagueId);

    const seasonId =
      Number(expectedSeasonId);

    if (
      !Number.isFinite(leagueId) ||
      leagueId <= 0
    ) {
      setError(
        "Bitte eine gültige Liga für die Expected Table auswählen."
      );
      return;
    }

    if (
      !Number.isFinite(seasonId) ||
      seasonId <= 0
    ) {
      setError(
        "Bitte eine gültige Saison für die Expected Table auswählen."
      );
      return;
    }

    setBuildingExpectedTable(true);
    setExpectedTableResult(null);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            league_id:
              leagueId,
            season_id:
              seasonId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "Expected Table konnte nicht aufgebaut werden."
        );
        return;
      }

      setExpectedTableResult(
        result
      );

      setExpectedSeasonState({
        success: true,

        possible: true,

        league_id:
          leagueId,

        season_id:
          seasonId,

        record_exists:
          true,

        expected_table_exists:
          true,

        record:
          result?.record ||
          null,

        message:
          result?.message ||
          "Expected Table aufgebaut.",
      });

      /*
       * Die ausführliche Diagnose wird NICHT
       * automatisch ein zweites Mal gestartet.
       * Die Build-Antwort kennt die aktuelle
       * Fixture-Vollständigkeit bereits.
       */
      setExpectedDiagnosis({
        success: true,

        possible: true,

        league_id:
          leagueId,

        league_name:
          result?.record?.league_name ||
          null,

        season_id:
          seasonId,

        season_name:
          result?.record?.season_name ||
          null,

        fixtures_played:
          Number(
            result?.fixtures_played ||
            0
          ),

        fixtures_with_expected_values:
          Number(
            result
              ?.fixtures_with_expected_values ||
            0
          ),

        fixtures_missing_expected_values:
          Number(
            result
              ?.fixtures_missing_expected_values ||
            0
          ),

        missing_fixture_ids:
          Array.isArray(
            result?.missing_fixture_ids
          )
            ? result.missing_fixture_ids
            : [],

        invalid_fixture_ids:
          Array.isArray(
            result?.invalid_fixture_ids
          )
            ? result.invalid_fixture_ids
            : [],

        expected_table_exists:
          true,

        expected_table_stored_fixtures:
          Number(
            result
              ?.fixtures_with_expected_values ||
            0
          ),

        expected_table_current:
          true,

        record:
          result?.record ||
          null,
      });
    } catch (err) {
      console.error(
        "[Admin Tools] Expected Table build",
        err
      );

      setError(
        err?.message ||
          "Expected Table konnte nicht aufgebaut werden."
      );
    } finally {
      setBuildingExpectedTable(false);
    }
  }


  async function calculateExpectedDetailedStatistics(
    method
  ) {
    const leagueId =
      Number(
        expectedLeagueId
      );

    const seasonId =
      Number(
        expectedSeasonId
      );

    if (
      !Number.isFinite(
        leagueId
      ) ||
      !Number.isFinite(
        seasonId
      )
    ) {
      setError(
        "Liga oder Saison ist ungültig."
      );

      return;
    }

    setCalculatingExpectedDetailedStats(
      true
    );

    setExpectedDetailedStatsResult(
      null
    );

    setError("");

    try {
      const response =
        await fetchViaPost(
          "build-user-season-stats",
          JSON.stringify({
            mode:
              "calculate_detailed_statistics",

            league_id:
              leagueId,

            season_id:
              seasonId,

            method,

            simulations:
              Number(
                expectedSimulationRuns ||
                1000
              ),

            simulation_drawfactor:
              Number(
                expectedSimulationDrawFactor
              ),
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
          "Detailed Statistics konnten nicht berechnet werden."
        );

        return;
      }

      setExpectedDetailedStatsResult(
        result
      );

      /*
       * Matrix/Simulation speichern einen
       * aktualisierten USER_SEASON_STATS Record.
       * Compare ausdrücklich nicht.
       */
      if (
        result?.saved &&
        result?.record
      ) {
        setExpectedTableResult({
          success: true,

          message:
            result?.message,

          record:
            result.record,
        });

        setExpectedSeasonState(
          (previous) => ({
            ...(previous || {}),

            success: true,
            record_exists: true,
            expected_table_exists: true,

            record:
              result.record,
          })
        );
      }
    } catch (err) {
      console.error(
        "[Admin Tools] Detailed Expected Statistics",
        err
      );

      setError(
        err?.message ||
        "Detailed Statistics konnten nicht berechnet werden."
      );
    } finally {
      setCalculatingExpectedDetailedStats(
        false
      );
    }
  }


  async function loadTeamStatsSeasons(
    leagueIdValue
  ) {
    const leagueId =
      Number(
        leagueIdValue
      );

    setTeamStatsSeasonId("");
    setTeamStatsSeasons([]);
    setTeamStatsResult(null);

    if (
      !Number.isFinite(
        leagueId
      )
    ) {
      return;
    }

    setLoadingTeamStatsSeasons(true);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "get-season-base-stats",
          JSON.stringify({
            mode:
              "list_seasons",

            league_id:
              leagueId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
          "Gespeicherte Saisons konnten nicht geladen werden."
        );
        return;
      }

      const seasons =
        (result?.seasons || [])
          .map(
            (season) => ({
              season_id:
                Number(
                  season
                    ?.season_id
                ),

              season_name:
                season
                  ?.season_name ||
                `Season ${season?.season_id}`,

              starting_at:
                season
                  ?.starting_at ||
                null,
            })
          )
          .filter(
            (season) =>
              Number.isFinite(
                season.season_id
              )
          );

      setTeamStatsSeasons(
        seasons
      );
    } catch (err) {
      console.error(
        "[Admin Tools] Team Statistics seasons",
        err
      );

      setError(
        err?.message ||
        "Gespeicherte Saisons konnten nicht geladen werden."
      );
    } finally {
      setLoadingTeamStatsSeasons(false);
    }
  }


  async function loadTeamStatistics(
    leagueIdValue =
      teamStatsLeagueId,
    seasonIdValue =
      teamStatsSeasonId
  ) {
    const leagueId =
      Number(
        leagueIdValue
      );

    const seasonId =
      Number(
        seasonIdValue
      );

    if (
      !Number.isFinite(
        leagueId
      ) ||
      !Number.isFinite(
        seasonId
      )
    ) {
      return;
    }

    setLoadingTeamStats(true);
    setTeamStatsResult(null);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "get-team-season-statistics",
          JSON.stringify({
            league_id:
              leagueId,

            season_id:
              seasonId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
          "Team Statistics konnten nicht geladen werden."
        );
        return;
      }

      setTeamStatsResult(
        result
      );
    } catch (err) {
      console.error(
        "[Admin Tools] Team Statistics",
        err
      );

      setError(
        err?.message ||
        "Team Statistics konnten nicht geladen werden."
      );
    } finally {
      setLoadingTeamStats(false);
    }
  }


  async function reloadMatchStatistics() {
    setReloadingMatchStatistics(true);
    setMatchStatisticsReloadResult(null);
    setError("");

    setMatchStatisticsReloadProgress({
      current: 0,
      total: 0,
      current_label:
        "Arbeitsliste wird vorbereitet …",
      fixtures_expected: 0,
      fixtures_with_statistics: 0,
      fixtures_complete: 0,
      errors: 0,
    });

    try {
      const prepareResponse =
        await fetchViaPost(
          "build-season-base-stats",
          JSON.stringify({
            mode:
              "prepare_match_statistics_reload",

            /*
             * Version-2-Records werden übersprungen.
             * Ein abgebrochener Lauf kann deshalb
             * einfach später fortgesetzt werden.
             */
            force:
              false,
          })
        );

      const prepared =
        await prepareResponse.json();

      if (!prepared?.success) {
        throw new Error(
          prepared?.message ||
          "Match-Statistics-Arbeitsliste konnte nicht vorbereitet werden."
        );
      }

      const items =
        Array.isArray(
          prepared?.items
        )
          ? prepared.items
          : [];

      const total =
        items.length;

      setMatchStatisticsReloadProgress({
        current: 0,
        total,
        current_label:
          total > 0
            ? "Startbereit"
            : "Keine offenen Records",
        fixtures_expected: 0,
        fixtures_with_statistics: 0,
        fixtures_complete: 0,
        errors: 0,
      });

      if (total === 0) {
        setMatchStatisticsReloadResult({
          total: 0,
          completed: 0,
          failed: 0,
          fixtures_expected: 0,
          fixtures_with_statistics: 0,
          fixtures_complete: 0,
          errors: [],
        });

        return;
      }

      let fixturesExpected = 0;
      let fixturesWithStatistics = 0;
      let fixturesComplete = 0;

      const errors = [];

      for (
        let index = 0;
        index < items.length;
        index += 1
      ) {
        const item =
          items[index];

        const label =
          `${item?.league_name || `Liga ${item?.league_id}`} · ` +
          `${item?.season_name || `Season ${item?.season_id}`}`;

        setMatchStatisticsReloadProgress({
          current:
            index,
          total,
          current_label:
            label,
          fixtures_expected:
            fixturesExpected,
          fixtures_with_statistics:
            fixturesWithStatistics,
          fixtures_complete:
            fixturesComplete,
          errors:
            errors.length,
        });

        try {
          const response =
            await fetchViaPost(
              "build-season-base-stats",
              JSON.stringify({
                mode:
                  "reload_match_statistics",

                league_id:
                  Number(
                    item.league_id
                  ),

                season_id:
                  Number(
                    item.season_id
                  ),
              })
            );

          const result =
            await response.json();

          if (!result?.success) {
            throw new Error(
              result?.message ||
              "Match Statistics konnten nicht neu geladen werden."
            );
          }

          fixturesExpected +=
            Number(
              result
                ?.fixtures_expected ||
              0
            );

          fixturesWithStatistics +=
            Number(
              result
                ?.fixtures_with_statistics ||
              0
            );

          fixturesComplete +=
            Number(
              result
                ?.fixtures_complete ||
              0
            );

        } catch (err) {
          console.error(
            "[Admin Tools] Match Statistics reload item",
            item,
            err
          );

          errors.push({
            league_id:
              item?.league_id,

            league_name:
              item?.league_name ||
              null,

            season_id:
              item?.season_id,

            season_name:
              item?.season_name ||
              null,

            message:
              err?.message ||
              String(err),
          });
        }

        setMatchStatisticsReloadProgress({
          current:
            index + 1,
          total,
          current_label:
            label,
          fixtures_expected:
            fixturesExpected,
          fixtures_with_statistics:
            fixturesWithStatistics,
          fixtures_complete:
            fixturesComplete,
          errors:
            errors.length,
        });
      }

      setMatchStatisticsReloadResult({
        total,
        completed:
          total -
          errors.length,
        failed:
          errors.length,
        fixtures_expected:
          fixturesExpected,
        fixtures_with_statistics:
          fixturesWithStatistics,
        fixtures_complete:
          fixturesComplete,
        errors,
      });

      if (
        teamStatsLeagueId &&
        teamStatsSeasonId
      ) {
        await loadTeamStatistics(
          teamStatsLeagueId,
          teamStatsSeasonId
        );
      }

    } catch (err) {
      console.error(
        "[Admin Tools] Match Statistics reload",
        err
      );

      setError(
        err?.message ||
        "Match Statistics konnten nicht neu geladen werden."
      );

    } finally {
      /*
       * Wichtig auch für Close/Escape:
       * der Busy-State wird garantiert beendet.
       */
      setReloadingMatchStatistics(false);
    }
  }


  async function prepareCurrentActualRepair() {
    setActualRepairPhase("prepare");
    setActualRepairItems([]);
    setActualRepairProgress({ current: 0, total: 0 });
    setError("");
    try {
      const listResponse = await fetchViaPost(
        "build-season-base-stats",
        JSON.stringify({ mode: "prepare_current_actual_repair_list" })
      );
      const list = await listResponse.json();
      if (!list?.success) throw new Error(list?.message || "Repair-Liste konnte nicht geladen werden.");
      const source = Array.isArray(list?.items) ? list.items : [];
      setActualRepairProgress({ current: 0, total: source.length });
      const prepared = [];
      for (let index = 0; index < source.length; index += 1) {
        const item = source[index];
        try {
          const response = await fetchViaPost(
            "build-season-base-stats",
            JSON.stringify({
              mode: "prepare_current_actual_repair_item",
              league_id: item.league_id,
              season_id: item.season_id,
            })
          );
          prepared.push(await response.json());
        } catch (err) {
          prepared.push({ ...item, status: "error", message: err?.message || String(err) });
        }
        setActualRepairItems([...prepared]);
        setActualRepairProgress({ current: index + 1, total: source.length });
      }
    } catch (err) {
      setError(err?.message || "Actual-Repair konnte nicht vorbereitet werden.");
    } finally {
      setActualRepairPhase("");
    }
  }


  async function applyCurrentActualRepair() {
    const repairable = actualRepairItems.filter((item) => item?.status === "repairable");
    setActualRepairPhase("apply");
    setActualRepairProgress({ current: 0, total: repairable.length });
    setError("");
    const applied = [];
    for (let index = 0; index < repairable.length; index += 1) {
      const item = repairable[index];
      try {
        const response = await fetchViaPost(
          "build-season-base-stats",
          JSON.stringify({
            mode: "apply_current_actual_repair_item",
            league_id: item.league_id,
            season_id: item.season_id,
            calculated_signature: item.calculated_signature,
          })
        );
        applied.push(await response.json());
      } catch (err) {
        applied.push({ ...item, status: "error", message: err?.message || String(err) });
      }
      setActualRepairProgress({ current: index + 1, total: repairable.length });
    }
    const bySeason = new Map(applied.map((item) => [`${item.league_id}:${item.season_id}`, item]));
    setActualRepairItems((current) => current.map((item) =>
      bySeason.get(`${item.league_id}:${item.season_id}`) || item
    ));
    setActualRepairPhase("");
  }


  async function recalculateTeamTransitionStats() {
    const leagueId =
      Number(transitionLeagueId);

    if (!Number.isFinite(leagueId) || leagueId <= 0) {
      setError(
        "Bitte eine gültige League ID eingeben."
      );
      return;
    }

    setRecalculatingTransition(true);
    setTransitionRecalculateResult(null);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "recalculate-team-transition-stats",
          JSON.stringify({
            league_id: leagueId,
          })
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "Team Transition Stats konnten nicht neu berechnet werden."
        );
        return;
      }

      setTransitionRecalculateResult(
        result
      );
    } catch (err) {
      console.error(
        "[Admin Tools] Team Transition recalculate",
        err
      );

      setError(
        err?.message ||
          "Team Transition Stats konnten nicht neu berechnet werden."
      );
    } finally {
      setRecalculatingTransition(false);
    }
  }


  async function inventoryLeagueReferenceStats() {
    setLoadingLeagueReferenceInventory(true);
    setLeagueReferenceInventory(null);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "inventory-league-reference-stats",
          JSON.stringify({})
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "League Reference Stats konnten nicht inventarisiert werden."
        );

        return;
      }

      setLeagueReferenceInventory(
        result
      );
    } catch (err) {
      console.error(
        "[Admin Tools] League Reference Stats inventory",
        err
      );

      setError(
        err?.message ||
          "League Reference Stats konnten nicht inventarisiert werden."
      );
    } finally {
      setLoadingLeagueReferenceInventory(false);
    }
  }


  async function diagnoseTeamTransitionStats() {
    setLoadingTransitionDiagnosis(true);
    setTransitionDiagnosis(null);
    setError("");

    try {
      const response =
        await fetchViaPost(
          "diagnose-team-transition-stats",
          JSON.stringify({})
        );

      const result =
        await response.json();

      if (!result?.success) {
        setError(
          result?.message ||
            "Team Transition Stats konnten nicht diagnostiziert werden."
        );

        return;
      }

      setTransitionDiagnosis(
        result
      );
    } catch (err) {
      console.error(
        "[Admin Tools] Team Transition diagnosis",
        err
      );

      setError(
        err?.message ||
          "Team Transition Stats konnten nicht diagnostiziert werden."
      );
    } finally {
      setLoadingTransitionDiagnosis(false);
    }
  }


  const summary =
    transitionDiagnosis?.summary ||
    {};

  const leagueCompetitions =
    leagues.filter(
      (league) =>
        String(
          league?.type || ""
        ).toLowerCase() === "league" &&
        String(
          league?.sub_type || ""
        ).toLowerCase() === "domestic"
    );


  const expectedDisplayRecord =
    expectedTableResult?.record ||
    expectedSeasonState?.record ||
    expectedDiagnosis?.record ||
    null;

  const expectedDisplayRows =
    normalizeExpectedTable(
      expectedDisplayRecord
        ?.expected_table ||
      []
    );

  const toolSectionSx = {
    p: 2.5,
    borderWidth: 2,
    borderRadius: 2,
    boxShadow: 1,
  };


  return (
    <Dialog
      open={open}
      onClose={
        loadingLeagueReferenceInventory ||
        loadingUserSeasonDiagnosis ||
        loadingTransitionDiagnosis ||
        loadingExpectedSeasons ||
        loadingExpectedDiagnosis ||
        buildingExpectedTable ||
        compilingMissingExpected ||
        loadingTeamStatsSeasons ||
        loadingTeamStats ||
        reloadingMatchStatistics ||
        actualRepairPhase ||
        recalculatingTransition
          ? undefined
          : onClose
      }
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        Admin · Tools
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={4}>
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Current Actual / Real Repair
                </Typography>
                <Typography color="text.secondary">
                  Prüft ausschließlich aktuelle globale SEASON_BASE_STATS. Prepare ist read-only;
                  Apply ersetzt nur validierte Actual Tables und Real-Aggregate. Split-Saisons
                  bleiben needs_review. User-, Expected- und Should-Daten werden nicht verändert.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={prepareCurrentActualRepair}
                  disabled={Boolean(actualRepairPhase)}
                >
                  Prepare (read-only)
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={applyCurrentActualRepair}
                  disabled={Boolean(actualRepairPhase) || !actualRepairItems.some((item) => item.status === "repairable")}
                >
                  Repairable anwenden
                </Button>
                {actualRepairPhase && <CircularProgress size={24} />}
              </Box>
              {(actualRepairPhase || actualRepairProgress.total > 0) && (
                <Box sx={{ display: "grid", gap: 0.75 }}>
                  <LinearProgress
                    variant="determinate"
                    value={actualRepairProgress.total
                      ? actualRepairProgress.current / actualRepairProgress.total * 100
                      : 0}
                  />
                  <Typography variant="body2">
                    {actualRepairPhase === "apply" ? "Apply" : "Prepare"}: {actualRepairProgress.current}
                    {" von "}{actualRepairProgress.total}
                  </Typography>
                </Box>
              )}
              {actualRepairItems.length > 0 && (
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>Liga / Saison</TableCell>
                      <TableCell align="right">Vorher</TableCell>
                      <TableCell align="right">Final</TableCell>
                      <TableCell align="right">Differenz</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {actualRepairItems.map((item, index) => (
                        <TableRow key={`${item.league_id}:${item.season_id}:${index}`}>
                          <TableCell>
                            {item.league_name || `Liga ${item.league_id}`}{" · "}
                            {item.season_name || `Saison ${item.season_id}`}
                            {item.message && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.message}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.before_fixtures ?? "–"}</TableCell>
                          <TableCell align="right">{item.final_fixtures ?? "–"}</TableCell>
                          <TableCell align="right">{item.difference ?? "–"}</TableCell>
                          <TableCell>{item.status || "error"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Paper>


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  USER_SEASON_STATS · Datenzustand
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Prüft alle USER_SEASON_STATS des
                  angemeldeten Users auf strukturelle
                  und rechnerische Konsistenz sowie
                  auf fehlende, bereits aufbaubare
                  Records. Die Diagnose ist read-only.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  onClick={
                    diagnoseUserSeasonStats
                  }
                  disabled={
                    loadingUserSeasonDiagnosis
                  }
                >
                  Meine Daten prüfen
                </Button>

                {loadingUserSeasonDiagnosis && (
                  <>
                    <CircularProgress
                      size={24}
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      USER_SEASON_STATS werden geprüft …
                    </Typography>
                  </>
                )}
              </Box>


              {userSeasonDiagnosis && (
                <>
                  <Alert
                    severity={
                      userSeasonDiagnosis.healthy
                        ? "success"
                        : (
                            Number(
                              userSeasonDiagnosis
                                ?.summary
                                ?.broken ||
                              0
                            ) > 0 ||
                            Number(
                              userSeasonDiagnosis
                                ?.summary
                                ?.inconsistent ||
                              0
                            ) > 0
                              ? "error"
                              : "warning"
                          )
                    }
                  >
                    {userSeasonDiagnosis.message}
                    {" · Read-only: "}
                    {userSeasonDiagnosis.read_only
                      ? "Ja"
                      : "Nein"}
                  </Alert>


                  <Box
                    sx={{
                      display: "grid",
                      gap: 0.75,
                      maxWidth: 760,
                    }}
                  >
                    <ResultRow
                      label="User"
                      value={
                        userSeasonDiagnosis
                          .user_id
                      }
                    />

                    <ResultRow
                      label="Vorhandene Records"
                      value={
                        userSeasonDiagnosis
                          ?.summary
                          ?.user_records_found
                      }
                    />

                    <ResultRow
                      label="Healthy"
                      value={
                        userSeasonDiagnosis
                          ?.summary
                          ?.healthy
                      }
                    />

                    <ResultRow
                      label="Warnungen"
                      value={
                        userSeasonDiagnosis
                          ?.summary
                          ?.warning
                      }
                    />

                    <ResultRow
                      label="Inkonsistent"
                      value={
                        userSeasonDiagnosis
                          ?.summary
                          ?.inconsistent
                      }
                    />

                    <ResultRow
                      label="Defekt / nicht lesbar"
                      value={
                        userSeasonDiagnosis
                          ?.summary
                          ?.broken
                      }
                    />

                    <ResultRow
                      label="Fehlend, aber aufbaubar"
                      value={
                        userSeasonDiagnosis
                          ?.summary
                          ?.buildable_missing_records
                      }
                    />
                  </Box>


                  {(
                    userSeasonDiagnosis
                      ?.records ||
                    []
                  ).some(
                    (record) =>
                      record.status !==
                      "healthy"
                  ) && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          mb: 1,
                        }}
                      >
                        Auffällige vorhandene Records
                      </Typography>

                      <Stack
                        spacing={1}
                        sx={{
                          maxHeight: 460,
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        {(
                          userSeasonDiagnosis
                            ?.records ||
                          []
                        )
                          .filter(
                            (record) =>
                              record.status !==
                              "healthy"
                          )
                          .map(
                            (
                              record,
                              index
                            ) => (
                              <Paper
                                key={
                                  record.key ||
                                  index
                                }
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                  }}
                                >
                                  {record.league_name ||
                                    `League ${record.league_id ?? "?"}`}
                                  {" · "}
                                  {record.season_name ||
                                    `Season ${record.season_id ?? "?"}`}
                                  {" · "}
                                  {record.status}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    mb: 0.75,
                                  }}
                                >
                                  Expected:{" "}
                                  {record
                                    ?.expected
                                    ?.exists
                                    ? "ja"
                                    : "nein"}
                                  {" · Should: "}
                                  {record
                                    ?.should
                                    ?.exists
                                    ? "ja"
                                    : "nein"}
                                  {" · Updated: "}
                                  {record.updated_at ||
                                    "—"}
                                </Typography>

                                {(
                                  record.issues ||
                                  []
                                ).map(
                                  (
                                    item,
                                    issueIndex
                                  ) => (
                                    <Typography
                                      key={
                                        `${item.code || "issue"}-${issueIndex}`
                                      }
                                      variant="body2"
                                      color={
                                        item.severity ===
                                        "error"
                                          ? "error"
                                          : "warning.main"
                                      }
                                      sx={{
                                        mb: 0.35,
                                      }}
                                    >
                                      {item.message}
                                    </Typography>
                                  )
                                )}
                              </Paper>
                            )
                          )}
                      </Stack>
                    </Box>
                  )}


                  {(
                    userSeasonDiagnosis
                      ?.missing_buildable_records ||
                    []
                  ).length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 800,
                          mb: 1,
                        }}
                      >
                        Fehlende, aber aufbaubare Records
                      </Typography>

                      <Stack
                        spacing={1}
                        sx={{
                          maxHeight: 360,
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        {(
                          userSeasonDiagnosis
                            ?.missing_buildable_records ||
                          []
                        ).map(
                          (
                            record,
                            index
                          ) => (
                            <Paper
                              key={
                                `${record.league_id}-${record.season_id}-${index}`
                              }
                              variant="outlined"
                              sx={{
                                p: 1.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                }}
                              >
                                {record.league_name ||
                                  `League ${record.league_id}`}
                                {" · "}
                                {record.season_name ||
                                  `Season ${record.season_id}`}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Expected aufbaubar:{" "}
                                {record.expected_possible
                                  ? "ja"
                                  : "nein"}
                                {" · Should aufbaubar: "}
                                {record.should_possible
                                  ? "ja"
                                  : "nein"}
                              </Typography>
                            </Paper>
                          )
                        )}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Paper>


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Season Base Stats
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Season Base Records prüfen,
                  fehlende Records aufbauen,
                  vorhandene Records anreichern
                  und den Aufbau fortsetzen.
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={onOpenSeasonBaseStats}
                disabled={
                  loadingTransitionDiagnosis
                }
                sx={{
                  alignSelf: "flex-start",
                }}
              >
                Season Base Stats öffnen
              </Button>
            </Stack>
          </Paper>


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Expected Table
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Prüft die verfügbaren Modell-Saisons
                  des angemeldeten Users und baut daraus
                  eine Expected Table auf.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 320,
                    flexGrow: 1,
                    maxWidth: 520,
                  }}
                >
                  <InputLabel id="expected-table-league-label">
                    League
                  </InputLabel>

                  <Select
                    labelId="expected-table-league-label"
                    value={expectedLeagueId}
                    label="League"
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setExpectedLeagueId(
                        value
                      );

                      setExpectedSeasonId("");
                      setExpectedSeasons([]);
                      setExpectedDiagnosis(null);
                      setExpectedTableResult(null);
                      setExpectedCompileResult(null);
                      setExpectedDetailedStatsOpen(false);
                      setExpectedDetailedStatsResult(null);
                      setExpectedCompileProgress({
                        current: 0,
                        total: 0,
                      });
                      setError("");

                      loadExpectedSeasons(
                        value
                      );
                    }}
                    disabled={
                      buildingExpectedTable
                    }
                  >
                    <MenuItem
                      value=""
                      disabled
                    >
                      Liga auswählen …
                    </MenuItem>

                    {leagueCompetitions.map(
                      (league) => (
                        <MenuItem
                          key={league.id}
                          value={Number(
                            league.id
                          )}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <LeagueDisplay
                              league={league}
                              compact
                            />

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                ml: "auto",
                                fontVariantNumeric:
                                  "tabular-nums",
                              }}
                            >
                              League ID {league.id}
                            </Typography>
                          </Box>
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 280,
                    flexGrow: 1,
                    maxWidth: 420,
                  }}
                  disabled={
                    !expectedLeagueId ||
                    loadingExpectedSeasons ||
                    expectedSeasons.length === 0 ||
                    buildingExpectedTable
                  }
                >
                  <InputLabel id="expected-table-season-label">
                    Saison
                  </InputLabel>

                  <Select
                    labelId="expected-table-season-label"
                    value={expectedSeasonId}
                    label="Saison"
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setExpectedSeasonId(
                        value
                      );

                      setExpectedDiagnosis(null);
                      setExpectedSeasonState(null);
                      setExpectedSeasonInfoOpen(false);
                      setExpectedTableResult(null);
                      setExpectedCompileResult(null);
                      setExpectedCompileProgress({
                        current: 0,
                        total: 0,
                      });
                      setError("");

                      loadExpectedSeasonState(
                        expectedLeagueId,
                        value
                      );
                    }}
                  >
                    <MenuItem
                      value=""
                      disabled
                    >
                      Saison auswählen …
                    </MenuItem>

                    {expectedSeasons.map(
                      (season) => (
                        <MenuItem
                          key={
                            season.season_id
                          }
                          value={
                            Number(
                              season.season_id
                            )
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              gap: 2,
                            }}
                          >
                            <Typography>
                              {season.season_name ||
                                `Season ${season.season_id}`}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                ml: "auto",
                                fontVariantNumeric:
                                  "tabular-nums",
                              }}
                            >
                              Season ID {season.season_id}
                            </Typography>
                          </Box>
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={
                    buildExpectedTable
                  }
                  disabled={
                    !expectedLeagueId ||
                    !expectedSeasonId ||
                    expectedSeasonState?.possible ===
                      false ||
                    buildingExpectedTable ||
                    compilingMissingExpected ||
                    loadingExpectedSeasons ||
                    loadingExpectedDiagnosis ||
                    loadingTransitionDiagnosis ||
                    recalculatingTransition
                  }
                >
                  {expectedSeasonState
                    ?.expected_table_exists
                      ? "Expected Table aktualisieren"
                      : "Expected Table aufbauen"}
                </Button>

                {(
                  buildingExpectedTable ||
                  loadingExpectedSeasons ||
                  loadingExpectedDiagnosis
                ) && (
                  <CircularProgress size={24} />
                )}
              </Box>

              {expectedLeagueId &&
                !loadingExpectedSeasons &&
                expectedSeasons.length === 0 && (
                  <Alert severity="info">
                    Für diese Liga liegen keine Saisons
                    mit vollständigen Season Parameters
                    und Team Strengths vor.
                  </Alert>
                )}

              {expectedSeasonId &&
                expectedSeasonState && (
                  <Alert
                    severity={
                      expectedSeasonState
                        ?.expected_table_exists
                        ? "success"
                        : (
                            expectedSeasonState
                              ?.possible
                              ? "info"
                              : "warning"
                          )
                    }
                  >
                    {expectedSeasonState
                      ?.expected_table_exists
                      ? (
                          `Expected Table gespeichert` +
                          (
                            expectedSeasonState
                              ?.record
                              ?.expected_table_summary
                            ? ` · ${Number(
                                expectedSeasonState
                                  .record
                                  .expected_table_summary
                                  .fixtures_with_expected_values ||
                                0
                              )} Fixtures berücksichtigt`
                            : ""
                          )
                        )
                      : (
                          expectedSeasonState
                            ?.message ||
                          "Für diese Saison ist noch keine Expected Table gespeichert."
                        )}
                  </Alert>
                )}


              {expectedSeasonId && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="text"
                    onClick={() => {
                      if (
                        expectedSeasonInfoOpen &&
                        expectedDiagnosis
                      ) {
                        setExpectedSeasonInfoOpen(
                          false
                        );

                        return;
                      }

                      diagnoseExpectedTable(
                        expectedLeagueId,
                        expectedSeasonId
                      );
                    }}
                    disabled={
                      loadingExpectedDiagnosis ||
                      buildingExpectedTable ||
                      compilingMissingExpected
                    }
                  >
                    {expectedSeasonInfoOpen
                      ? "Saisoninformationen ausblenden"
                      : "Saisoninformationen prüfen"}
                  </Button>

                  {loadingExpectedDiagnosis && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Vollständigkeit wird geprüft …
                    </Typography>
                  )}
                </Box>
              )}


              {expectedSeasonInfoOpen &&
                expectedDiagnosis && (
                <Box
                  sx={{
                    display: "grid",
                    gap: 0.75,
                    maxWidth: 700,
                  }}
                >
                  <Alert
                    severity={
                      expectedDiagnosis.possible
                        ? (
                            expectedDiagnosis
                              .fixtures_missing_expected_values >
                            0
                              ? "warning"
                              : "success"
                          )
                        : "info"
                    }
                  >
                    {expectedDiagnosis.possible
                      ? (
                          `${expectedDiagnosis.fixtures_with_expected_values || 0} ` +
                          `von ${expectedDiagnosis.fixtures_played || 0} ` +
                          `gespielten Regular-Season-Fixtures besitzen Expected Values.`
                        )
                      : (
                          expectedDiagnosis.message ||
                          "Expected Table kann für diese Saison nicht aufgebaut werden."
                        )}
                  </Alert>

                  <ResultRow
                    label="Liga"
                    value={
                      expectedDiagnosis.league_name
                    }
                  />

                  <ResultRow
                    label="Saison"
                    value={
                      expectedDiagnosis.season_name
                    }
                  />

                  <ResultRow
                    label="Gespielte Regular-Season-Spiele"
                    value={
                      expectedDiagnosis.fixtures_played
                    }
                  />

                  <ResultRow
                    label="Mit Expected Values"
                    value={
                      expectedDiagnosis
                        .fixtures_with_expected_values
                    }
                  />

                  <ResultRow
                    label="Fehlende Expected Values"
                    value={
                      expectedDiagnosis
                        .fixtures_missing_expected_values
                    }
                  />

                  <Divider />

                  <ResultRow
                    label="Expected Table vorhanden"
                    value={
                      expectedDiagnosis.expected_table_exists
                        ? "Ja"
                        : "Nein"
                    }
                  />

                  {expectedDiagnosis.expected_table_exists && (
                    <>
                      <ResultRow
                        label="Im gespeicherten Record berücksichtigt"
                        value={
                          expectedDiagnosis
                            .expected_table_stored_fixtures
                        }
                      />

                      <ResultRow
                        label="Expected Table aktuell"
                        value={
                          expectedDiagnosis.expected_table_current
                            ? "Ja"
                            : "Nein"
                        }
                      />
                    </>
                  )}
                </Box>
              )}

              {expectedDiagnosis?.possible &&
                Number(
                  expectedDiagnosis
                    ?.missing_fixture_ids
                    ?.length ||
                  0
                ) > 0 && (
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1.25,
                      maxWidth: 700,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={
                        compileMissingExpectedFixtures
                      }
                      disabled={
                        compilingMissingExpected ||
                        buildingExpectedTable ||
                        loadingExpectedDiagnosis ||
                        loadingExpectedSeasons
                      }
                      sx={{
                        justifySelf:
                          "start",
                      }}
                    >
                      Fehlende Spiele nachkompilieren
                    </Button>

                    {compilingMissingExpected && (
                      <Box
                        sx={{
                          display: "grid",
                          gap: 0.75,
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={
                            expectedCompileProgress.total >
                            0
                              ? (
                                  expectedCompileProgress.current /
                                  expectedCompileProgress.total
                                ) *
                                100
                              : 0
                          }
                        />

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {
                            expectedCompileProgress.current
                          }
                          {" von "}
                          {
                            expectedCompileProgress.total
                          }
                          {" Fixtures nachkompiliert"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

              {expectedCompileResult && (
                <Alert
                  severity={
                    expectedCompileResult
                      .failed_fixture_ids
                      ?.length
                      ? "warning"
                      : "success"
                  }
                >
                  {expectedCompileResult.compiled}
                  {" von "}
                  {expectedCompileResult.requested}
                  {" fehlenden Fixtures wurden nachkompiliert."}

                  {expectedCompileResult
                    .failed_fixture_ids
                    ?.length > 0
                    ? (
                        ` Fehlgeschlagen: ${expectedCompileResult
                          .failed_fixture_ids
                          .join(", ")}.`
                      )
                    : ""}
                </Alert>
              )}

            </Stack>
          </Paper>


          {expectedDisplayRows.length > 0 && (
            <LeagueTablePanel
              rows={expectedDisplayRows}
              decimalValues
              title={
                `Expected Table · ${
                  expectedDisplayRecord
                    ?.league_name ||
                  ""
                }`
              }
              subtitle={
                expectedDisplayRecord
                  ?.season_name ||
                ""
              }
              showTableMode
              initialTableMode="overall"
              renderStats
            />
          )}


          {expectedDisplayRows.length > 0 && (
            <Paper
              variant="outlined"
              sx={{
                ...toolSectionSx,
                mt: -2,
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                    >
                      Detailed Statistics
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Zusätzliche Prüfgrößen für
                      Modell und Saison.
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      setExpectedDetailedStatsOpen(
                        (value) =>
                          !value
                      )
                    }
                  >
                    {expectedDetailedStatsOpen
                      ? "Detailed Statistics ausblenden"
                      : "Detailed Statistics anzeigen"}
                  </Button>
                </Box>


                {expectedDetailedStatsOpen && (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 0.75,
                        maxWidth: 760,
                      }}
                    >
                      <ResultRow
                        label="Fixtures"
                        value={
                          expectedDisplayRecord
                            ?.model_statistics
                            ?.probability_fixture_count ??
                          "—"
                        }
                      />

                      <ResultRow
                        label="Average Expected Probability"
                        value={
                          Number.isFinite(
                            Number(
                              expectedDisplayRecord
                                ?.model_statistics
                                ?.average_expected_probability
                            )
                          )
                            ? `${(
                                Number(
                                  expectedDisplayRecord
                                    .model_statistics
                                    .average_expected_probability
                                ) *
                                100
                              ).toFixed(3)} %`
                            : "—"
                        }
                      />

                      <ResultRow
                        label="Average Occurred Probability"
                        value={
                          Number.isFinite(
                            Number(
                              expectedDisplayRecord
                                ?.model_statistics
                                ?.average_occurred_probability
                            )
                          )
                            ? `${(
                                Number(
                                  expectedDisplayRecord
                                    .model_statistics
                                    .average_occurred_probability
                                ) *
                                100
                              ).toFixed(3)} %`
                            : "—"
                        }
                      />

                      <ResultRow
                        label="Differenz Occurred − Expected"
                        value={
                          Number.isFinite(
                            Number(
                              expectedDisplayRecord
                                ?.model_statistics
                                ?.average_expected_probability
                            )
                          ) &&
                          Number.isFinite(
                            Number(
                              expectedDisplayRecord
                                ?.model_statistics
                                ?.average_occurred_probability
                            )
                          )
                            ? `${(
                                (
                                  Number(
                                    expectedDisplayRecord
                                      .model_statistics
                                      .average_occurred_probability
                                  ) -
                                  Number(
                                    expectedDisplayRecord
                                      .model_statistics
                                      .average_expected_probability
                                  )
                                ) *
                                100
                              ).toFixed(3)} %-Pkt.`
                            : "—"
                        }
                      />

                      <Divider />

                      <ResultRow
                        label="Average Goal Deviation Expected"
                        value={
                          Number.isFinite(
                            Number(
                              expectedDisplayRecord
                                ?.model_statistics
                                ?.average_expected_goal_deviation
                            )
                          )
                            ? Number(
                                expectedDisplayRecord
                                  .model_statistics
                                  .average_expected_goal_deviation
                              ).toFixed(4)
                            : "—"
                        }
                      />

                      <ResultRow
                        label="Average Goal Deviation Occurred"
                        value={
                          Number.isFinite(
                            Number(
                              expectedDisplayRecord
                                ?.model_statistics
                                ?.average_occurred_goal_deviation
                            )
                          )
                            ? Number(
                                expectedDisplayRecord
                                  .model_statistics
                                  .average_occurred_goal_deviation
                              ).toFixed(4)
                            : "—"
                        }
                      />

                      {expectedDisplayRecord
                        ?.model_statistics
                        ?.goal_deviation_method && (
                          <ResultRow
                            label="Berechnungsmethode"
                            value={
                              expectedDisplayRecord
                                .model_statistics
                                .goal_deviation_method
                            }
                          />
                        )}
                    </Box>


                    <Alert severity="info">
                      Die Goal-Deviation-Werte
                      vergleichen die vom Modell
                      erwartete durchschnittliche
                      Torabweichung mit der tatsächlich
                      eingetretenen Abweichung.
                      Die Berechnung muss gesondert
                      angestoßen werden und kann einen
                      Moment dauern.
                    </Alert>


                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        label="Simulation Draw Factor"
                        size="small"
                        type="number"
                        value={
                          expectedSimulationDrawFactor
                        }
                        onChange={(event) =>
                          setExpectedSimulationDrawFactor(
                            event.target.value
                          )
                        }
                        inputProps={{
                          step: 0.01,
                          min: 0.5,
                          max: 1.5,
                        }}
                        sx={{
                          width: 190,
                        }}
                      />

                      <TextField
                        label="Simulation Runs"
                        size="small"
                        type="number"
                        value={
                          expectedSimulationRuns
                        }
                        onChange={(event) =>
                          setExpectedSimulationRuns(
                            event.target.value
                          )
                        }
                        inputProps={{
                          step: 100,
                          min: 100,
                          max: 100000,
                        }}
                        sx={{
                          width: 160,
                        }}
                      />

                      <Button
                        variant="contained"
                        onClick={() =>
                          calculateExpectedDetailedStatistics(
                            "matrix"
                          )
                        }
                        disabled={
                          calculatingExpectedDetailedStats
                        }
                      >
                        Matrix
                      </Button>

                      <Button
                        variant="contained"
                        onClick={() =>
                          calculateExpectedDetailedStatistics(
                            "simulation"
                          )
                        }
                        disabled={
                          calculatingExpectedDetailedStats
                        }
                      >
                        Simulation
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={() =>
                          calculateExpectedDetailedStatistics(
                            "compare"
                          )
                        }
                        disabled={
                          calculatingExpectedDetailedStats
                        }
                      >
                        Compare both
                      </Button>

                      {calculatingExpectedDetailedStats && (
                        <>
                          <CircularProgress
                            size={24}
                          />

                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            Detailed Statistics werden
                            berechnet …
                          </Typography>
                        </>
                      )}
                    </Box>


                    {expectedDetailedStatsResult && (
                      <Alert
                        severity="success"
                      >
                        {expectedDetailedStatsResult.message}
                      </Alert>
                    )}


                    {expectedDetailedStatsResult
                      ?.method ===
                      "compare" && (
                        <Box
                          sx={{
                            display: "grid",
                            gap: 0.75,
                            maxWidth: 760,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 800,
                            }}
                          >
                            Matrix vs. Simulation
                          </Typography>

                          <ResultRow
                            label="Fixtures"
                            value={
                              expectedDetailedStatsResult
                                ?.fixture_count
                            }
                          />

                          <ResultRow
                            label="Simulationen je Fixture"
                            value={
                              expectedDetailedStatsResult
                                ?.simulations_per_fixture
                            }
                          />

                          <ResultRow
                            label="Goal Deviation · Occurred"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.average_occurred_goal_deviation
                              ).toFixed(6)
                            }
                          />

                          <ResultRow
                            label="Expected Goal Deviation · Matrix"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.matrix
                                  ?.average_expected_goal_deviation
                              ).toFixed(6)
                            }
                          />

                          <ResultRow
                            label="Expected Goal Deviation · Simulation"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.simulation
                                  ?.average_expected_goal_deviation
                              ).toFixed(6)
                            }
                          />

                          <ResultRow
                            label="Differenz Simulation − Matrix"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.comparison
                                  ?.average_difference
                              ).toFixed(6)
                            }
                          />

                          <ResultRow
                            label="Mittlere absolute Fixture-Differenz"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.comparison
                                  ?.mean_absolute_fixture_difference
                              ).toFixed(6)
                            }
                          />

                          <ResultRow
                            label="Größte Fixture-Differenz"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.comparison
                                  ?.max_absolute_fixture_difference
                              ).toFixed(6)
                            }
                          />

                          <Divider />

                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 800,
                            }}
                          >
                            Remis-Vergleich
                          </Typography>

                          <ResultRow
                            label="Matrix Draw Factor"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.matrix_drawfactor
                              ).toFixed(3)
                            }
                          />

                          <ResultRow
                            label="Simulation Draw Factor"
                            value={
                              Number(
                                expectedDetailedStatsResult
                                  ?.simulation_drawfactor
                              ).toFixed(3)
                            }
                          />

                          <ResultRow
                            label="Remis · tatsächlich"
                            value={
                              `${(
                                Number(
                                  expectedDetailedStatsResult
                                    ?.actual
                                    ?.draw_rate
                                ) *
                                100
                              ).toFixed(3)} %`
                            }
                          />

                          <ResultRow
                            label="Remis · Matrix erwartet"
                            value={
                              `${(
                                Number(
                                  expectedDetailedStatsResult
                                    ?.matrix
                                    ?.average_draw_probability
                                ) *
                                100
                              ).toFixed(3)} %`
                            }
                          />

                          <ResultRow
                            label="Matrix − tatsächlich"
                            value={
                              `${(
                                Number(
                                  expectedDetailedStatsResult
                                    ?.matrix
                                    ?.draw_probability_difference_to_actual
                                ) *
                                100
                              ).toFixed(3)} %-Pkt.`
                            }
                          />

                          <ResultRow
                            label="Remis · Simulation erwartet"
                            value={
                              `${(
                                Number(
                                  expectedDetailedStatsResult
                                    ?.simulation
                                    ?.average_draw_probability
                                ) *
                                100
                              ).toFixed(3)} %`
                            }
                          />

                          <ResultRow
                            label="Simulation − tatsächlich"
                            value={
                              `${(
                                Number(
                                  expectedDetailedStatsResult
                                    ?.simulation
                                    ?.draw_probability_difference_to_actual
                                ) *
                                100
                              ).toFixed(3)} %-Pkt.`
                            }
                          />

                          <Divider />

                          <ResultRow
                            label="Matrix · Laufzeit"
                            value={
                              `${Number(
                                expectedDetailedStatsResult
                                  ?.matrix
                                  ?.duration_ms
                              ).toFixed(1)} ms`
                            }
                          />

                          <ResultRow
                            label="Simulation · Laufzeit"
                            value={
                              `${Number(
                                expectedDetailedStatsResult
                                  ?.simulation
                                  ?.duration_ms
                              ).toFixed(1)} ms`
                            }
                          />

                          <ResultRow
                            label="Simulation / Matrix · Zeitfaktor"
                            value={
                              Number.isFinite(
                                Number(
                                  expectedDetailedStatsResult
                                    ?.comparison
                                    ?.simulation_to_matrix_time_factor
                                )
                              )
                                ? `${Number(
                                    expectedDetailedStatsResult
                                      .comparison
                                      .simulation_to_matrix_time_factor
                                  ).toFixed(2)} ×`
                                : "—"
                            }
                          />

                          <ResultRow
                            label="Gesamtlaufzeit"
                            value={
                              `${Number(
                                expectedDetailedStatsResult
                                  ?.total_duration_ms
                              ).toFixed(1)} ms`
                            }
                          />
                        </Box>
                      )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2.5}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Team Statistics
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Aggregiert die gespeicherten
                  Match Statistics nach Team und
                  berechnet daraus zusätzlich die
                  Should Goals.
                </Typography>
              </Box>


              <Box
                sx={{
                  display: "grid",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={
                      reloadMatchStatistics
                    }
                    disabled={
                      reloadingMatchStatistics ||
                      loadingTeamStats
                    }
                  >
                    Match Statistics neu laden
                  </Button>

                  {reloadingMatchStatistics && (
                    <CircularProgress
                      size={24}
                    />
                  )}
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Lädt ausschließlich die Match Statistics
                  der vorhandenen Season-Base-Records neu.
                  Tabellen, Transitions, Play-offs und
                  sonstige Season-Base-Daten bleiben
                  unverändert.
                </Typography>

                {(
                  reloadingMatchStatistics ||
                  matchStatisticsReloadProgress.total > 0
                ) && (
                  <Box
                    sx={{
                      display: "grid",
                      gap: 0.75,
                      maxWidth: 850,
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={
                        matchStatisticsReloadProgress.total > 0
                          ? (
                              matchStatisticsReloadProgress.current /
                              matchStatisticsReloadProgress.total
                            ) * 100
                          : 0
                      }
                    />

                    <Typography
                      variant="body2"
                      sx={{
                        fontVariantNumeric:
                          "tabular-nums",
                      }}
                    >
                      {
                        matchStatisticsReloadProgress.current
                      }
                      {" von "}
                      {
                        matchStatisticsReloadProgress.total
                      }
                      {" Season-Base-Records"}
                      {
                        matchStatisticsReloadProgress.current_label
                          ? ` · ${matchStatisticsReloadProgress.current_label}`
                          : ""
                      }
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontVariantNumeric:
                          "tabular-nums",
                      }}
                    >
                      Fixtures geprüft:{" "}
                      {
                        matchStatisticsReloadProgress.fixtures_expected
                      }
                      {" · mit Statistics: "}
                      {
                        matchStatisticsReloadProgress.fixtures_with_statistics
                      }
                      {" · vollständig: "}
                      {
                        matchStatisticsReloadProgress.fixtures_complete
                      }
                      {" · Fehler: "}
                      {
                        matchStatisticsReloadProgress.errors
                      }
                    </Typography>
                  </Box>
                )}

                {matchStatisticsReloadResult && (
                  <Alert
                    severity={
                      matchStatisticsReloadResult.failed > 0
                        ? "warning"
                        : "success"
                    }
                  >
                    {matchStatisticsReloadResult.total === 0
                      ? (
                          "Alle vorhandenen Match-Statistics-Records sind bereits auf dem aktuellen Stand."
                        )
                      : (
                          `${matchStatisticsReloadResult.completed} von ` +
                          `${matchStatisticsReloadResult.total} Records neu geladen. ` +
                          `${matchStatisticsReloadResult.fixtures_with_statistics} von ` +
                          `${matchStatisticsReloadResult.fixtures_expected} Fixtures mit Statistics. ` +
                          `${matchStatisticsReloadResult.failed} Fehler.`
                        )}
                  </Alert>
                )}
              </Box>


              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 320,
                    flexGrow: 1,
                    maxWidth: 520,
                  }}
                >
                  <InputLabel id="team-statistics-league-label">
                    League
                  </InputLabel>

                  <Select
                    labelId="team-statistics-league-label"
                    value={teamStatsLeagueId}
                    label="League"
                    disabled={
                      loadingTeamStats ||
                      reloadingMatchStatistics
                    }
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setTeamStatsLeagueId(
                        value
                      );

                      setTeamStatsSeasonId("");
                      setTeamStatsSeasons([]);
                      setTeamStatsResult(null);
                      setError("");

                      loadTeamStatsSeasons(
                        value
                      );
                    }}
                  >
                    <MenuItem
                      value=""
                      disabled
                    >
                      Liga auswählen …
                    </MenuItem>

                    {leagueCompetitions.map(
                      (league) => (
                        <MenuItem
                          key={league.id}
                          value={
                            Number(
                              league.id
                            )
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <LeagueDisplay
                              league={league}
                              compact
                            />

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                ml: "auto",
                                fontVariantNumeric:
                                  "tabular-nums",
                              }}
                            >
                              League ID {league.id}
                            </Typography>
                          </Box>
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>


                <FormControl
                  size="small"
                  sx={{
                    minWidth: 280,
                    flexGrow: 1,
                    maxWidth: 420,
                  }}
                  disabled={
                    !teamStatsLeagueId ||
                    loadingTeamStatsSeasons ||
                    teamStatsSeasons.length === 0 ||
                    loadingTeamStats
                  }
                >
                  <InputLabel id="team-statistics-season-label">
                    Saison
                  </InputLabel>

                  <Select
                    labelId="team-statistics-season-label"
                    value={teamStatsSeasonId}
                    label="Saison"
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      setTeamStatsSeasonId(
                        value
                      );

                      setTeamStatsResult(null);
                      setError("");

                      loadTeamStatistics(
                        teamStatsLeagueId,
                        value
                      );
                    }}
                  >
                    <MenuItem
                      value=""
                      disabled
                    >
                      Saison auswählen …
                    </MenuItem>

                    {teamStatsSeasons.map(
                      (season) => (
                        <MenuItem
                          key={
                            season.season_id
                          }
                          value={
                            season.season_id
                          }
                        >
                          {season.season_name}

                          {season.starting_at
                            ? ` – ${String(
                                season.starting_at
                              ).slice(0, 10)}`
                            : ""}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>


                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                  }}
                  disabled={
                    !teamStatsResult
                  }
                >
                  <InputLabel id="team-statistics-mode-label">
                    Bereich
                  </InputLabel>

                  <Select
                    labelId="team-statistics-mode-label"
                    value={teamStatsMode}
                    label="Bereich"
                    onChange={(event) =>
                      setTeamStatsMode(
                        event.target.value
                      )
                    }
                  >
                    <MenuItem value="overall">
                      Total
                    </MenuItem>

                    <MenuItem value="home">
                      Home
                    </MenuItem>

                    <MenuItem value="away">
                      Away
                    </MenuItem>
                  </Select>
                </FormControl>

                {(
                  loadingTeamStatsSeasons ||
                  loadingTeamStats
                ) && (
                  <CircularProgress
                    size={24}
                  />
                )}
              </Box>


              {teamStatsLeagueId &&
                !loadingTeamStatsSeasons &&
                teamStatsSeasons.length === 0 && (
                  <Alert severity="info">
                    Für diese Liga liegen keine
                    gespeicherten Season-Base-Saisons vor.
                  </Alert>
                )}


              {loadingTeamStats && (
                <LinearProgress />
              )}


              {teamStatsResult && (
                <>
                  <Alert
                    severity={
                      Number(
                        teamStatsResult
                          ?.diagnostics
                          ?.fixtures_without_team_assignment ||
                        0
                      ) > 0
                        ? "warning"
                        : "success"
                    }
                  >
                    {teamStatsResult.message}
                    {" "}
                    Should Goals verfügbar für{" "}
                    {
                      teamStatsResult
                        ?.diagnostics
                        ?.fixtures_with_should_goals
                    }
                    {" von "}
                    {
                      teamStatsResult
                        ?.diagnostics
                        ?.fixtures_with_statistics
                    }
                    {" Statistik-Fixtures."}
                  </Alert>

                  <Box
                    sx={{
                      display: "grid",
                      gap: 0.75,
                      maxWidth: 700,
                    }}
                  >
                    <ResultRow
                      label="Liga"
                      value={
                        teamStatsResult.league_name
                      }
                    />

                    <ResultRow
                      label="Saison"
                      value={
                        teamStatsResult.season_name
                      }
                    />

                    <ResultRow
                      label="Gespeicherte Statistik-Fixtures"
                      value={
                        teamStatsResult
                          ?.diagnostics
                          ?.fixtures_stored
                      }
                    />

                    <ResultRow
                      label="Verwendete Fixtures"
                      value={
                        teamStatsResult
                          ?.diagnostics
                          ?.fixtures_used
                      }
                    />

                    <ResultRow
                      label="Ohne Team-Zuordnung"
                      value={
                        teamStatsResult
                          ?.diagnostics
                          ?.fixtures_without_team_assignment
                      }
                    />

                    <ResultRow
                      label="Should Goals berechenbar"
                      value={
                        teamStatsResult
                          ?.diagnostics
                          ?.fixtures_with_should_goals
                      }
                    />
                  </Box>


                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 800,
                      mt: 1,
                    }}
                  >
                    {teamStatsResult.league_name}
                    {" · "}
                    {teamStatsResult.season_name}
                    {" · "}
                    {teamStatsMode === "overall"
                      ? "Total"
                      : teamStatsMode === "home"
                        ? "Home"
                        : "Away"}
                  </Typography>

                  <TeamStatisticsTable
                    result={teamStatsResult}
                    tableMode={teamStatsMode}
                  />
                </>
              )}
            </Stack>
          </Paper>


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  League Reference Stats · Inventur
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Liest den vollständigen Bestand aus
                  LEAGUE_REFERENCE_STATS. Es werden keine
                  Datensätze verändert, ergänzt oder gelöscht.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                onClick={
                  inventoryLeagueReferenceStats
                }
                disabled={
                  loadingLeagueReferenceInventory
                }
                sx={{
                  alignSelf: "flex-start",
                }}
              >
                Inventur starten
              </Button>

              {loadingLeagueReferenceInventory && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress size={24} />

                  <Typography>
                    LEAGUE_REFERENCE_STATS werden gelesen …
                  </Typography>
                </Box>
              )}

              {leagueReferenceInventory && (
                <>
                  <Alert severity="info">
                    {leagueReferenceInventory.message}
                    {" · Read-only: "}
                    {leagueReferenceInventory.read_only
                      ? "Ja"
                      : "Nein"}
                  </Alert>

                  <Box
                    sx={{
                      display: "grid",
                      gap: 0.75,
                      maxWidth: 760,
                    }}
                  >
                    <ResultRow
                      label="Keys gesamt"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.keys_total
                      }
                    />

                    <ResultRow
                      label="Gültige JSON-Records"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.valid_json
                      }
                    />

                    <ResultRow
                      label="Ungültige JSON-Records"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.invalid_json
                      }
                    />

                    <Divider />

                    <ResultRow
                      label="Finale / Legacy Records"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.final_or_legacy_records
                      }
                    />

                    <ResultRow
                      label="Progress Records"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.progress_records
                      }
                    />

                    <Divider />

                    <ResultRow
                      label="Mit actual"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.records_with_actual
                      }
                    />

                    <ResultRow
                      label="Mit model_comparison"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.records_with_model_comparison
                      }
                    />

                    <ResultRow
                      label="Mit fixation"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.records_with_fixation
                      }
                    />

                    <ResultRow
                      label="Mit should_goals_comparison"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.records_with_should_goals
                      }
                    />

                    <ResultRow
                      label="Mit fixture_odds_diagnostics"
                      value={
                        leagueReferenceInventory
                          ?.summary
                          ?.records_with_fixture_odds_diagnostics
                      }
                    />
                  </Box>


                  <Divider />


                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                      }}
                    >
                      Records nach User
                    </Typography>

                    <Stack spacing={0.5}>
                      {Object.entries(
                        leagueReferenceInventory
                          ?.summary
                          ?.users ||
                        {}
                      )
                        .sort(
                          (a, b) =>
                            Number(b[1]) -
                            Number(a[1])
                        )
                        .map(
                          ([userId, count]) => (
                            <ResultRow
                              key={userId}
                              label={userId}
                              value={count}
                            />
                          )
                        )}
                    </Stack>
                  </Box>


                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                      }}
                    >
                      Schema-Varianten
                    </Typography>

                    <Stack spacing={1}>
                      {Object.entries(
                        leagueReferenceInventory
                          ?.summary
                          ?.schema_signatures ||
                        {}
                      )
                        .sort(
                          (a, b) =>
                            Number(b[1]) -
                            Number(a[1])
                        )
                        .map(
                          ([signature, count]) => (
                            <Paper
                              key={signature}
                              variant="outlined"
                              sx={{
                                p: 1.25,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                }}
                              >
                                {count} Records
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  wordBreak:
                                    "break-word",
                                }}
                              >
                                {signature}
                              </Typography>
                            </Paper>
                          )
                        )}
                    </Stack>
                  </Box>


                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        mb: 1,
                      }}
                    >
                      Einzelrecords
                    </Typography>

                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{
                        maxHeight: 600,
                      }}
                    >
                      <Table
                        stickyHeader
                        size="small"
                        sx={{
                          minWidth: 1250,
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              User
                            </TableCell>

                            <TableCell>
                              Typ
                            </TableCell>

                            <TableCell>
                              League
                            </TableCell>

                            <TableCell>
                              Saison
                            </TableCell>

                            <TableCell align="right">
                              Bytes
                            </TableCell>

                            <TableCell align="right">
                              Fixtures
                            </TableCell>

                            <TableCell>
                              actual
                            </TableCell>

                            <TableCell>
                              model
                            </TableCell>

                            <TableCell>
                              fixation
                            </TableCell>

                            <TableCell>
                              should
                            </TableCell>

                            <TableCell>
                              fixture diag
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {(
                            leagueReferenceInventory
                              ?.records ||
                            []
                          ).map(
                            (record, index) => (
                              <TableRow
                                key={
                                  record.key ||
                                  index
                                }
                                hover
                              >
                                <TableCell>
                                  {record.user_id ??
                                    "—"}
                                </TableCell>

                                <TableCell>
                                  {record.record_type ??
                                    "—"}
                                </TableCell>

                                <TableCell>
                                  {record.league_name ||
                                    record.league_id ||
                                    "—"}
                                </TableCell>

                                <TableCell>
                                  {record.season_name ||
                                    record.season_id ||
                                    "—"}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {record.raw_bytes ??
                                    "—"}
                                </TableCell>

                                <TableCell
                                  align="right"
                                >
                                  {record.fixtures_played ??
                                    record.fixtures_calculated ??
                                    "—"}
                                </TableCell>

                                <TableCell>
                                  {record
                                    ?.completeness
                                    ?.has_actual
                                    ? "✓"
                                    : "—"}
                                </TableCell>

                                <TableCell>
                                  {record
                                    ?.completeness
                                    ?.has_model_comparison
                                    ? "✓"
                                    : "—"}
                                </TableCell>

                                <TableCell>
                                  {record
                                    ?.completeness
                                    ?.has_fixation
                                    ? "✓"
                                    : "—"}
                                </TableCell>

                                <TableCell>
                                  {record
                                    ?.completeness
                                    ?.has_should_goals_comparison
                                    ? "✓"
                                    : "—"}
                                </TableCell>

                                <TableCell>
                                  {record
                                    ?.completeness
                                    ?.has_fixture_odds_diagnostics
                                    ? "✓"
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>


          <Paper
            variant="outlined"
            sx={toolSectionSx}
          >
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Team Transition Stats
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  Prüft den gesamten Bestand auf
                  Vollständigkeit für die spätere
                  Aufsteiger- und Absteiger-Auswertung.
                  Die Diagnose verändert keine Daten.
                </Typography>
              </Box>


              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="League ID"
                  size="small"
                  value={transitionLeagueId}
                  onChange={(event) =>
                    setTransitionLeagueId(
                      event.target.value
                    )
                  }
                  sx={{ width: 150 }}
                />

                <Button
                  variant="contained"
                  onClick={
                    recalculateTeamTransitionStats
                  }
                  disabled={
                    recalculatingTransition ||
                    loadingTransitionDiagnosis
                  }
                >
                  Recalculate
                </Button>

                {recalculatingTransition && (
                  <CircularProgress size={24} />
                )}
              </Box>


              {transitionRecalculateResult && (
                <Alert severity="success">
                  {transitionRecalculateResult.message}
                </Alert>
              )}


              <Button
                variant="outlined"
                onClick={
                  diagnoseTeamTransitionStats
                }
                disabled={
                  loadingTransitionDiagnosis ||
                  recalculatingTransition
                }
                sx={{
                  alignSelf: "flex-start",
                }}
              >
                Diagnose starten
              </Button>


              {loadingTransitionDiagnosis && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress
                    size={24}
                  />

                  <Typography>
                    TEAM_TRANSITION_STATS
                    werden geprüft …
                  </Typography>
                </Box>
              )}


              {transitionDiagnosis && (
                <>
                  <Alert
                    severity={
                      Number(
                        summary.records_incomplete ||
                        0
                      ) === 0 &&
                      Number(
                        summary.duplicate_groups ||
                        0
                      ) === 0 &&
                      Number(
                        summary.read_errors ||
                        0
                      ) === 0
                        ? "success"
                        : "warning"
                    }
                  >
                    {transitionDiagnosis.message}
                  </Alert>


                  <Box
                    sx={{
                      display: "grid",
                      gap: 0.75,
                      maxWidth: 650,
                    }}
                  >
                    <ResultRow
                      label="Records gesamt"
                      value={
                        summary.records_total
                      }
                    />

                    <ResultRow
                      label="Vollständig"
                      value={
                        summary.records_complete
                      }
                    />

                    <ResultRow
                      label="Unvollständig"
                      value={
                        summary.records_incomplete
                      }
                    />

                    <Divider />

                    <ResultRow
                      label="Aufsteiger gesamt"
                      value={
                        summary.promoted_total
                      }
                    />

                    <ResultRow
                      label="Aufsteiger vollständig"
                      value={
                        summary.promoted_complete
                      }
                    />

                    <ResultRow
                      label="Absteiger gesamt"
                      value={
                        summary.relegated_total
                      }
                    />

                    <ResultRow
                      label="Absteiger vollständig"
                      value={
                        summary.relegated_complete
                      }
                    />

                    <Divider />

                    <ResultRow
                      label="Duplikat-Gruppen"
                      value={
                        summary.duplicate_groups
                      }
                    />

                    <ResultRow
                      label="Records in Duplikat-Gruppen"
                      value={
                        summary.duplicate_records
                      }
                    />

                    <ResultRow
                      label="Lesefehler"
                      value={
                        summary.read_errors
                      }
                    />
                  </Box>


                  {Object.keys(
                    transitionDiagnosis
                      .problem_counts ||
                    {}
                  ).length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Gefundene Probleme
                      </Typography>

                      <Stack spacing={0.5}>
                        {Object.entries(
                          transitionDiagnosis
                            .problem_counts
                        ).map(
                          ([
                            problem,
                            count,
                          ]) => (
                            <ResultRow
                              key={problem}
                              label={problem}
                              value={count}
                            />
                          )
                        )}
                      </Stack>
                    </Box>
                  )}


                  {(
                    transitionDiagnosis
                      .incomplete_records ||
                    []
                  ).length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Unvollständige Records
                      </Typography>

                      <Stack spacing={1}>
                        {transitionDiagnosis
                          .incomplete_records
                          .map(
                            (
                              item,
                              index
                            ) => (
                              <Paper
                                key={
                                  item.key ||
                                  index
                                }
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                  }}
                                >
                                  {item.team_name ||
                                    `Team ${item.team_id || "?"}`}
                                  {" · "}
                                  {item.transition_type ||
                                    "Transition unbekannt"}
                                </Typography>

                                <Typography
                                  variant="body2"
                                >
                                  {item.to_league_name ||
                                    `League ${item.to_league_id || "?"}`}
                                  {" · "}
                                  {item.to_season_name ||
                                    `Season ${item.to_season_id || "?"}`}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  color="error"
                                  sx={{
                                    mt: 0.5,
                                  }}
                                >
                                  {(
                                    item.problems ||
                                    []
                                  ).join(
                                    " · "
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    wordBreak:
                                      "break-all",
                                  }}
                                >
                                  {item.key}
                                </Typography>
                              </Paper>
                            )
                          )}
                      </Stack>
                    </Box>
                  )}


                  {(
                    transitionDiagnosis
                      .duplicates ||
                    []
                  ).length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Verdächtige Duplikate
                      </Typography>

                      <Stack spacing={1}>
                        {transitionDiagnosis
                          .duplicates
                          .map(
                            (
                              group,
                              index
                            ) => (
                              <Paper
                                key={
                                  group.duplicate_key ||
                                  index
                                }
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                  }}
                                >
                                  {group.team_name ||
                                    `Team ${group.team_id || "?"}`}
                                  {" · "}
                                  {group.transition_type}
                                </Typography>

                                <Typography
                                  variant="body2"
                                >
                                  {group.to_league_name ||
                                    `League ${group.to_league_id}`}
                                  {" · "}
                                  {group.to_season_name ||
                                    `Season ${group.to_season_id}`}
                                  {" · "}
                                  {group.count}
                                  {" Records"}
                                </Typography>

                                {(group.keys || [])
                                  .map(
                                    (
                                      key
                                    ) => (
                                      <Typography
                                        key={key}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          display:
                                            "block",
                                          wordBreak:
                                            "break-all",
                                        }}
                                      >
                                        {key}
                                      </Typography>
                                    )
                                  )}
                              </Paper>
                            )
                          )}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>


      <DialogActions>
        <Button
          onClick={onClose}
          disabled={
            loadingLeagueReferenceInventory ||
            loadingUserSeasonDiagnosis ||
            loadingTransitionDiagnosis ||
            loadingExpectedSeasons ||
            loadingExpectedDiagnosis ||
            buildingExpectedTable ||
            compilingMissingExpected ||
            loadingTeamStatsSeasons ||
            loadingTeamStats ||
            reloadingMatchStatistics ||
            recalculatingTransition
          }
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
